import React from 'react';

import { Button as ChakraButton, forwardRef } from '@chakra-ui/react';

import { normalizeSize } from './normalize-size';
import { normalizeVariant } from './normalize-variant';
import { ButtonProps } from './types';
import { ButtonSizeCanonical } from './types/button-size';

/** Maps canonical button size to the DLS Icon `size` token. */
const ICON_SIZE_MAP: Record<ButtonSizeCanonical, string> = {
  xl: 'small',
  lg: 'small',
  md: 'small',
  sm: 'extra-small',
};

/**
 * Returns the DLS Icon `size` token for icon elements based on the resolved
 * button size. xl/lg/md → 'small'; sm → 'extra-small'.
 * Falls back to 'small' for responsive objects or undefined.
 */
export function getIconSize(size: ButtonProps['size']): string {
  if (typeof size === 'string') {
    return ICON_SIZE_MAP[size as ButtonSizeCanonical] ?? 'small';
  }
  return 'small';
}

function withIconSize(
  icon: React.ReactElement | undefined,
  iconSize: string
): React.ReactElement | undefined {
  if (icon == null) return icon;
  // Preserve explicit sizing already set by the caller.
  if (icon.props.size != null) {
    return icon;
  }
  return React.cloneElement(icon, { size: iconSize });
}

export const Button = forwardRef<ButtonProps, 'button'>(function Button(
  {
    isDisabled,
    isVisuallyDisabled,
    size,
    variant,
    leftIcon,
    rightIcon,
    ...props
  },
  ref
) {
  const normalizedSize = normalizeSize(size);
  const iconSize = getIconSize(normalizedSize);

  return (
    <ChakraButton
      ref={ref}
      data-visually-disabled={
        isVisuallyDisabled && !isDisabled ? '' : undefined
      }
      isDisabled={isDisabled || undefined}
      iconSpacing={props.children != null ? '2' : '0'}
      size={normalizedSize}
      variant={normalizeVariant(variant)}
      leftIcon={withIconSize(leftIcon, iconSize)}
      rightIcon={withIconSize(rightIcon, iconSize)}
      {...props}
    />
  );
});

Button.displayName = 'Button';
